import { Chain, ChainType } from '@ankr.com/chains-list';
import { useEffect, useMemo, useState } from 'react';

import { getFallbackEndpointGroup } from 'modules/endpoints/constants/groups';
import { getGroupIdByChainId } from 'modules/endpoints/utils/getGroupByChainId';
import { mergeTacTendermintGroups } from 'modules/endpoints/utils/mergeTacTendermintGroups';
import { mergeTendermintsGroups } from 'modules/endpoints/utils/mergeTendermintsGroups';
import { mergeTonGroups } from 'modules/endpoints/utils/mergeTonGroups';
import { mergeExtensions } from 'modules/endpoints/utils/mergeExtensions';
import { useGroupedEndpoints } from 'modules/endpoints/hooks/useGrouppedEndpoints';
import { useSubchainID } from 'modules/chains/hooks/useSubchainID';

interface UseGroupsProps {
  chain: Chain;
  chainType: ChainType;
  shouldExpandMainnets?: boolean;
  shouldExpandTestnets?: boolean;
  shouldMergeTacTendermintGroups?: boolean;
  shouldMergeTendermintGroups?: boolean;
  shouldMergeTonGroups?: boolean;
  shouldMergeExtensions?: boolean;
}

export const useGroups = ({
  chain,
  chainType,
  shouldExpandMainnets,
  shouldExpandTestnets,
  shouldMergeExtensions,
  shouldMergeTacTendermintGroups,
  shouldMergeTendermintGroups,
  shouldMergeTonGroups,
}: UseGroupsProps) => {
  const groupedEndpoints = useGroupedEndpoints(
    chain,
    shouldExpandMainnets,
    shouldExpandTestnets,
  );
  const groupEndpoints = groupedEndpoints[chainType];

  const subchainID = useSubchainID();

  const mergeGroups = () => {
    let mergedGroups = [...groupEndpoints];

    if (shouldMergeExtensions) {
      return mergeExtensions(mergedGroups);
    }

    if (shouldMergeTendermintGroups) {
      mergedGroups = mergeTendermintsGroups(mergedGroups);
    }

    if (shouldMergeTonGroups) {
      mergedGroups = mergeTonGroups(mergedGroups);
    }

    if (shouldMergeTacTendermintGroups) {
      mergedGroups = mergeTacTendermintGroups(mergedGroups);
    }

    return mergedGroups;
  };

  const [groups, setGroups] = useState(mergeGroups());

  const [groupID, setGroupID] = useState(
    getGroupIdByChainId(subchainID) || groups[0]?.id,
  );

  const group = useMemo(
    () =>
      groups.find(({ id }) => id === groupID) ||
      getFallbackEndpointGroup(chain.name),
    [chain.name, groupID, groups],
  );

  useEffect(() => {
    const mergedGroups = mergeGroups();

    setGroupID(getGroupIdByChainId(subchainID) || mergedGroups[0]?.id);

    setGroups(mergedGroups);

    // only chain and chainType should be tracked
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chain, chainType]);

  return { group, groupedEndpoints, groups, setGroupID };
};
